import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {Contract} from '@shared/api/models/query/contract.model';

/**
 * UNIVAPLAT-3524: Delete action for the General details tab of the contract detail page.
 * The parent only renders it for DELETE_CONTRACT and a non-deleted contract; the API enforces both.
 * The backend owns the error text (422 contract_linked_to_organisations / contract_read_only) and it is
 * shown verbatim. No contract data is logged.
 */
@Component({
    selector: 'cdss-contract-delete-panel',
    standalone: true,
    imports: [ConfirmDialogDirective],
    templateUrl: './contract-delete-panel.component.html',
})
export class ContractDeletePanelComponent {
    @Input() contract!: Contract;
    /** Emitted once the contract is deleted; the page navigates back to the list. */
    @Output() deleted = new EventEmitter<void>();

    /** Double-submit guard; also disables the button while the request runs. */
    deleting = false;

    constructor(
        private contractsService: ContractsService,
        private toastr: ToastrService,
    ) {
    }

    get linkedOrganisations() {
        return this.contract?.contractOrganisations ?? [];
    }

    get isLinked(): boolean {
        return this.linkedOrganisations.length > 0;
    }

    async delete(): Promise<void> {
        if (this.deleting || !this.contract || this.isLinked) {
            return;
        }
        this.deleting = true;
        try {
            await this.contractsService.delete(this.contract.id);
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error || error?.error?.message || 'Something went wrong. Please try again.');
            }
            this.deleting = false;
            return;
        }
        this.toastr.success('Contract deleted', 'Success');
        this.deleted.emit();
    }
}
